// Pure derivations for O/D Analytics.
//
// Everything here works off the `tripList[]` that GET /v3/trip returns. Units, straight
// from the BigQuery projection in horizon-insights/utilities/bigquery.js:
//   totalDistance / avgDistance   metres
//   totalDuration / avgDuration   seconds
//   startTime / endTime           genuine UTC ISO-8601
//   idlingRatio                   unitless 0..1
//   totalFuelLitre, tripfuelLitre litres (same value, aliased twice upstream)
//   energyUsedPerTrip             kWh
//   origin / destination          pre-flattened human strings, nullable
//   originGeo / destinationGeo    "lat, lon" TEXT — see parseLatLonText
//   tripGeo                       GeoJSON LineString [lon, lat], null when the
//                                 source lineString_geo is null
//
// Day bucketing takes an explicit IANA timezone because agency data is authoritative
// for report timestamps — never the browser (see config/function/agencyTimezone.js).

import moment from 'moment-timezone';

export const UNKNOWN_PLACE = 'Unknown';

/** metres → km, rounded to 1 dp. */
export const toKm = (metres) => {
    const value = Number(metres);
    if (!Number.isFinite(value)) return 0;
    return Math.round((value / 1000) * 10) / 10;
};

/** Seconds → "2 hours 5 minutes". Empty string for 0/undefined. */
export const parseReadableDuration = (seconds) => {
    const total = Math.max(0, Math.round(Number(seconds) || 0));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = total % 60;

    const parts = [];
    if (hours > 0) parts.push(`${hours} ${hours > 1 ? 'hours' : 'hour'}`);
    if (minutes > 0) parts.push(`${minutes} ${minutes > 1 ? 'minutes' : 'minute'}`);
    // Only show seconds when they are the whole story — "2 hours 5 minutes 3 seconds" is noise.
    if (secs > 0 && hours === 0 && minutes === 0) parts.push(`${secs} ${secs > 1 ? 'seconds' : 'second'}`);
    return parts.join(' ');
};

/**
 * Seconds → compact "2h 05m" for dense table cells.
 *
 * `withSeconds` appends the remainder — "2h 05m 03s" — for the Dayboard's Drive, Idle and Excess
 * columns, where a day is compared against a driver's own timesheet and the rounded minute is the
 * difference being queried. Under a minute already reads as seconds either way.
 */
export const compactDuration = (seconds, withSeconds = false) => {
    const total = Math.max(0, Math.round(Number(seconds) || 0));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const tail = withSeconds ? ` ${String(total % 60).padStart(2, '0')}s` : '';
    if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m${tail}`;
    if (minutes > 0) return `${minutes}m${tail}`;
    return `${total}s`;
};

/**
 * originGeo / destinationGeo arrive as "lat, lon" text at 6 dp — LATITUDE FIRST, the
 * opposite axis order to tripGeo.coordinates. This is the one place that flip happens.
 * Returns [lon, lat] for map consumers, or null when unparseable.
 */
export const parseLatLonText = (value) => {
    if (typeof value !== 'string') return null;
    const parts = value.split(',');
    if (parts.length !== 2) return null;
    const lat = Number(parts[0].trim());
    const lon = Number(parts[1].trim());
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
    return [lon, lat];
};

/**
 * The calendar day a date-picker value represents, as 'YYYY-MM-DD'.
 *
 * react-multi-date-picker hands back DateObject instances, and moment() cannot parse one:
 * it finds no keys it recognises and silently falls back to *now*, so every request asked
 * for today no matter what was picked. DateObject exposes its own format(), which gives
 * the day literally shown in the calendar with no timezone in the way — use that when
 * present, and fall back to moment for a raw Date, epoch or string.
 */
export const pickedCalendarDay = (value) => {
    // new Date(null) is epoch 0, not an invalid date — nullish has to be rejected first
    // or an empty picker slot silently becomes 1970-01-01.
    if (value === null || value === undefined || value === '') return null;

    if (typeof value.format === 'function') {
        const formatted = value.format('YYYY-MM-DD');
        if (/^\d{4}-\d{2}-\d{2}$/.test(formatted)) return formatted;
    }
    const parsed = moment(value instanceof Date ? value : new Date(value));
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

/**
 * Turn the picker's two-value range into the request window, with each boundary anchored
 * to the agency timezone — the day the user clicked, start of day to end of day, in the
 * agency's own reckoning rather than the browser's.
 *
 * Returns null when the range is incomplete or unparseable, so the caller can refuse to
 * fire a request rather than silently query the wrong window.
 */
export const toRequestWindow = (date, timezone) => {
    if (!Array.isArray(date) || date.length < 2) return null;
    const zone = timezone || 'UTC';

    const fromDay = pickedCalendarDay(date[0]);
    const toDay = pickedCalendarDay(date[1]);
    if (!fromDay || !toDay) return null;

    // A reversed selection is a picker quirk, not a user intent to query nothing.
    const [startDay, endDay] = fromDay <= toDay ? [fromDay, toDay] : [toDay, fromDay];

    const from = moment.tz(startDay, 'YYYY-MM-DD', zone).startOf('day');
    const to = moment.tz(endDay, 'YYYY-MM-DD', zone).endOf('day');
    if (!from.isValid() || !to.isValid()) return null;

    return { fromTime: from.format(), toTime: to.format() };
};

const placeLabel = (value) => {
    if (typeof value !== 'string') return UNKNOWN_PLACE;
    const trimmed = value.trim();
    return trimmed === '' ? UNKNOWN_PLACE : trimmed;
};

const num = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * A real number, or null — never the 0 that `num` floors a missing value to.
 *
 * That floor is right for a sum and wrong for a reading. A coordinate is the sharpest case: 0 is
 * a real place, so a device that reported no fix would land on Null Island off the coast of
 * Ghana. A missing g-force reading has the same problem in miniature — `0.00 g` reads as a
 * measured non-event rather than as no measurement. Same vocabulary as the server's
 * `fuelLedger.numberOrNull`.
 */
export const numberOrNull = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Duration-weighted mean of idlingRatio, as a percentage.
 * A plain mean over-weights a 3-minute trip against an 8-hour one.
 *
 * Trips with no idlingRatio are excluded from both numerator and denominator, not
 * counted as zero — an absent signal is unknown idling, and folding it in as 0 would
 * quietly report the fleet as cleaner than the data supports. Note Number(null) is 0
 * and passes isFinite, so the nullish check has to come first.
 */
export const weightedIdleShare = (trips) => {
    if (!Array.isArray(trips) || trips.length === 0) return 0;
    let weighted = 0;
    let totalDuration = 0;
    trips.forEach((trip) => {
        const duration = num(trip?.totalDuration);
        if (duration <= 0) return;
        if (trip?.idlingRatio === null || trip?.idlingRatio === undefined || trip?.idlingRatio === '') return;
        const ratio = Number(trip.idlingRatio);
        if (!Number.isFinite(ratio)) return;
        weighted += ratio * duration;
        totalDuration += duration;
    });
    if (totalDuration === 0) return 0;
    return Math.round((weighted / totalDuration) * 1000) / 10;
};

/**
 * `fuelTrips` from GET /v3/trip/analytics, indexed by tripId. Accepts an already-built index
 * unchanged, so a caller can build it once and hand it down.
 */
export const toFuelIndex = (fuelTrips) => {
    if (fuelTrips instanceof Map) return fuelTrips;
    return new Map(
        (Array.isArray(fuelTrips) ? fuelTrips : [])
            .filter((row) => row?.tripId)
            .map((row) => [row.tripId, row]),
    );
};

/**
 * Litres for a trip set, preferring the reconciled figure from GET /v3/trip/analytics.
 *
 * **Load-bearing for every CAN asset, not an enhancement.** `/v3/trip`'s own `tripfuelLitre` is
 * `fuelLitre_start_int - fuelLitre_end_int` off the trips table, and a CAN vehicle populates neither:
 * measured across a week of dev traffic, `can.fuelLevelLitre` is null on 100% of samples and
 * `fuelLitre_start_int` on 100% of their trips, while `can.fuelLevelPerc` and `fuelPerc_start_int`
 * are both populated. The ledger is what turns that percentage into litres (percent × tank capacity,
 * or the policy's map equation), so for a CAN fleet these rows are the *only* fuel figure there is.
 *
 * Beyond that it also repairs OBD trips: the raw figure is start-level minus end-level clamped at
 * zero, so a trip refuelled mid-journey reports 0 L.
 *
 * The fallback is per trip, not per set: an asset whose ledger is BLOCKED sits beside one that
 * reconciled cleanly, and dropping the whole set to the naive figure would discard the good number to
 * match the bad one.
 *
 * @returns {{litres: number, reconciled: number}} `reconciled` counts trips that used the ledger.
 */
const fuelLitres = (trips, fuelByTrip) => (Array.isArray(trips) ? trips : []).reduce((acc, trip) => {
    const ledger = fuelByTrip.get(trip?.startTraceId);
    if (Number.isFinite(ledger?.litres)) {
        return { litres: acc.litres + ledger.litres, reconciled: acc.reconciled + 1 };
    }
    return {
        litres: acc.litres + num(trip?.totalFuelLitre ?? trip?.tripfuelLitre),
        reconciled: acc.reconciled,
    };
}, { litres: 0, reconciled: 0 });

/**
 * Engine-on time split into driving and idling, from each trip's own idlingRatio.
 *
 * Trips with no idlingRatio contribute to neither half — the same exclusion weightedIdleShare
 * makes, for the same reason: an absent signal is unknown idling, not zero.
 */
const engineOnSplit = (trips) => (Array.isArray(trips) ? trips : []).reduce((acc, trip) => {
    const duration = num(trip?.totalDuration);
    if (duration <= 0) return acc;
    if (trip?.idlingRatio === null || trip?.idlingRatio === undefined || trip?.idlingRatio === '') return acc;
    const ratio = Number(trip.idlingRatio);
    if (!Number.isFinite(ratio)) return acc;
    acc.idleSec += ratio * duration;
    acc.driveSec += (1 - ratio) * duration;
    return acc;
}, { idleSec: 0, driveSec: 0 });

const idleSeconds = (trips) => engineOnSplit(trips).idleSec;

/**
 * Distance-weighted mean eco score over a trip set, 0-10. Null when nothing carried one.
 *
 * Weighted by distance, not a mean of means: averaging per-trip scores would let a two-minute hop
 * outweigh a 400 km run. The 0.1 km floor keeps a stationary trip from contributing zero weight and
 * vanishing from the average entirely.
 *
 * Null rather than 0 when no trip has a score — 0 is the *worst* eco score, so reporting it for
 * missing data would flag a careful fleet as reckless.
 */
const weightedEcoScore = (trips) => {
    let total = 0;
    let weight = 0;
    (Array.isArray(trips) ? trips : []).forEach((trip) => {
        if (trip?.ecoScore === null || trip?.ecoScore === undefined || trip?.ecoScore === '') return;
        const score = Number(trip.ecoScore);
        if (!Number.isFinite(score)) return;
        const km = Math.max(num(trip.mileage) || toKm(num(trip.totalDistance)), 0.1);
        total += score * km;
        weight += km;
    });
    return weight > 0 ? total / weight : null;
};

/**
 * Sum the numeric facts of a trip set once, so every view reads the same totals.
 *
 * `fuelTrips` is optional only so a caller with no analytics payload still gets distance and
 * duration — omitting it reports 0 L for any CAN asset, which is why every view passes it.
 */
export const summariseTrips = (trips, fuelTrips) => {
    const list = Array.isArray(trips) ? trips : [];
    const totals = list.reduce((acc, trip) => {
        acc.distanceM += num(trip?.totalDistance);
        acc.durationSec += num(trip?.totalDuration);
        acc.energyKwh += num(trip?.energyUsedPerTrip);
        return acc;
    }, {
        distanceM: 0, durationSec: 0, energyKwh: 0,
    });
    const fuel = fuelLitres(list, toFuelIndex(fuelTrips));
    // Drive and idle are the two halves of engine-on time, taken only over trips that carry an
    // idlingRatio — the same exclusion weightedIdleShare makes. A trip with no idling signal
    // contributes to neither, so `driveSec + idleSec` is measured engine-on time, not total
    // duration, and the two never disagree with the Idle share bar sitting beside them.
    const split = engineOnSplit(list);

    return {
        trips: list.length,
        distanceKm: toKm(totals.distanceM),
        durationSec: totals.durationSec,
        driveSec: Math.round(split.driveSec),
        idleSec: Math.round(split.idleSec),
        fuelLitre: Math.round(fuel.litres * 100) / 100,
        // 'reconciled' | 'partial' | 'fallback' — an export compared against an older report needs to
        // know which trips came from the ledger and which from the trip record.
        fuelBasis: (fuel.reconciled === 0 && 'fallback')
            || (fuel.reconciled === list.length && 'reconciled')
            || 'partial',
        energyKwh: Math.round(totals.energyKwh * 100) / 100,
        idleSharePct: weightedIdleShare(list),
        ecoScore: weightedEcoScore(list),
        avgDistanceKm: list.length ? toKm(totals.distanceM / list.length) : 0,
        avgDurationSec: list.length ? Math.round(totals.durationSec / list.length) : 0,
    };
};

/** Sum one field of an analytics list, grouped by the `assetName` mergeAnalytics stamps on. */
const foldByAsset = (rows, pick) => {
    const byAsset = new Map();
    (Array.isArray(rows) ? rows : []).forEach((row) => {
        const key = row?.assetName;
        if (!key) return;
        byAsset.set(key, (byAsset.get(key) || 0) + num(pick(row)));
    });
    return byAsset;
};

/** Per 100 km, or null under a kilometre — a rate off 200 m of travel is noise, not a measure. */
const per100Km = (value, distanceKm) => (distanceKm > 1 ? (100 * value) / distanceKm : null);

/**
 * One row per asset for the Overview table. `results` is the shape odAnalyticsFetch
 * returns, so an asset whose fetch failed keeps its row and carries the reason.
 *
 * `analytics` is the optional merged `/v3/trip/analytics` payload. It supplies the four columns the
 * trip list cannot: excess idling, safety events, drains, and the ghost count's companion figures.
 * Without it those read null and the table shows an em dash rather than a confident zero — an
 * asset with no analytics has not been proven clean.
 *
 * `ghostTrips` comes off the trip response instead, because ghosts are filtered out of `tripList`
 * by the status selection and only the server knows how many were dropped.
 */
export const buildAssetSummary = (results, fuelTrips, analytics) => {
    if (!Array.isArray(results)) return [];
    const fuelByTrip = toFuelIndex(fuelTrips);

    const hasAnalytics = Boolean(analytics?.loaded);
    const excessByAsset = foldByAsset(analytics?.episodes, (row) => row.excessSec);
    // Every idle episode in the window, in-trip and between trips alike. The trip list can only
    // see the in-trip half; measured on W2037W over June 2026 the other half was 12.1 h of engine
    // running while parked — the most actionable number on this page, and previously invisible.
    const idleByAsset = foldByAsset(analytics?.episodes, (row) => row.durationSec);
    const eventsByAsset = foldByAsset(analytics?.events, () => 1);
    const drains = (analytics?.fuelEvents || []).filter((row) => row?.type === 'DRAIN');
    const drainCountByAsset = foldByAsset(drains, () => 1);
    const drainLitreByAsset = foldByAsset(drains, (row) => row.litres);

    return results.map((result) => {
        const trips = result?.trips || [];
        const summary = summariseTrips(trips, fuelByTrip);
        const assetName = result?.asset?.id || '-';
        const events = hasAnalytics ? (eventsByAsset.get(assetName) || 0) : null;
        // Window-wide idle when the analytics payload is loaded; the trip-derived in-trip figure
        // otherwise, which is the only thing available and is an undercount, not a different unit.
        const idleSec = hasAnalytics
            ? Math.round(idleByAsset.get(assetName) || 0)
            : summary.idleSec;
        // `driveSec` is in-trip moving time and `idleSec` is all idling, so the two are disjoint
        // and their sum is engine-on time. fleetconsole's equivalent adds whole trip durations to
        // the same idle figure, counting in-trip idling twice.
        const engineOnSec = summary.driveSec + idleSec;

        return {
            id: result?.asset?.uuid || result?.asset?.id,
            assetName,
            deviceSerial: result?.asset?.name || '',
            error: result?.error || null,
            ...summary,
            idleSec,
            engineOnSec,
            idleSharePct: engineOnSec > 0 ? (100 * idleSec) / engineOnSec : 0,
            // Ghosts are excluded from every figure above, exactly as fleetconsole excludes them —
            // the count exists so a journey missing from the table has a visible reason.
            ghostTrips: num(result?.ghostTrips),
            excessSec: hasAnalytics ? Math.round(excessByAsset.get(assetName) || 0) : null,
            events,
            eventsPer100: events === null ? null : per100Km(events, summary.distanceKm),
            drains: hasAnalytics ? (drainCountByAsset.get(assetName) || 0) : null,
            drainLitre: hasAnalytics
                ? Math.round((drainLitreByAsset.get(assetName) || 0) * 10) / 10
                : null,
            litresPer100: per100Km(summary.fuelLitre, summary.distanceKm),
            kwhPer100: per100Km(summary.energyKwh, summary.distanceKm),
        };
    });
};

/**
 * Fold a window-scoped analytics list (idle episodes, driving events) into agency-local
 * days, so the dayboard can join them onto its trip rows.
 *
 * Rows are bucketed by the day they START in.
 * ponytail: an idle episode running through midnight is credited whole to its start day.
 * Split it by overlap if long cross-midnight idles start distorting a day's excess.
 */
const foldByLocalDay = (rows, zone, pick, fold) => {
    const byDay = new Map();
    (Array.isArray(rows) ? rows : []).forEach((row) => {
        if (!row?.startTime) return;
        const at = moment.tz(row.startTime, zone);
        if (!at.isValid()) return;
        const key = at.format('YYYY-MM-DD');
        byDay.set(key, fold(byDay.get(key) || 0, num(pick(row))));
    });
    return byDay;
};

/**
 * Idle episodes folded into local days by wall-clock **overlap**, not by the day they started.
 *
 * An episode is a span, and this fleet idles for up to 27 hours at a stretch. Crediting one whole
 * to its start day is how a 24-hour day reports 34 hours of idling — a bug fleetconsole hit and
 * fixed on this very vehicle. `excessSec` rides along pro-rata on the same overlap share, so the
 * two columns stay consistent with each other.
 *
 * Instant-shaped rows (events, refuels, drains) belong to the day they happened and use
 * `foldByLocalDay` instead.
 *
 * @returns {Map<string, {idleSec: number, excessSec: number}>}
 */
const foldIdleByLocalDay = (episodes, zone) => {
    const byDay = new Map();
    const add = (key, idleSec, excessSec) => {
        const entry = byDay.get(key) || { idleSec: 0, excessSec: 0 };
        entry.idleSec += idleSec;
        entry.excessSec += excessSec;
        byDay.set(key, entry);
    };

    (Array.isArray(episodes) ? episodes : []).forEach((episode) => {
        if (!episode?.startTime) return;
        const start = moment.tz(episode.startTime, zone);
        if (!start.isValid()) return;

        const durationSec = num(episode.durationSec);
        const excessSec = num(episode.excessSec);
        const endMs = episode.endTime
            ? moment.tz(episode.endTime, zone).valueOf()
            : start.valueOf() + durationSec * 1000;
        const totalMs = Math.max(endMs - start.valueOf(), durationSec * 1000);

        // A zero-length or unparseable span still has to land somewhere.
        if (!(totalMs > 0)) {
            add(start.format('YYYY-MM-DD'), durationSec, excessSec);
            return;
        }

        const finish = start.valueOf() + totalMs;
        let cursor = start.clone();
        while (cursor.valueOf() < finish) {
            const nextMidnight = cursor.clone().startOf('day').add(1, 'day').valueOf();
            const segmentEnd = Math.min(finish, nextMidnight);
            const segmentMs = segmentEnd - cursor.valueOf();
            if (segmentMs <= 0) break;
            add(cursor.format('YYYY-MM-DD'), segmentMs / 1000, excessSec * (segmentMs / totalMs));
            cursor = moment.tz(segmentEnd, zone);
        }
    });

    return byDay;
};

/** Count rows matching `keep` into agency-local days. Instants, so no splitting. */
const countByLocalDay = (rows, zone, keep) => foldByLocalDay(
    (Array.isArray(rows) ? rows : []).filter(keep), zone, () => 1, (a, b) => a + b,
);

/**
 * When each trip actually pulled away, keyed on tripId — from `speed.trips[].firstMotionTime`.
 *
 * A trip record opens at ignition-on, not at first motion. Measured on 860896051940971 for
 * 2026-08-06, all seven of the day's trips stood still first — 28s at best, 9m 29s at worst — so the
 * Dayboard's "first" was reporting a time the vehicle was parked.
 *
 * Served rather than derived here: this cannot be recovered from the idle episodes in the same
 * payload. `kmh` is null across the whole lead-in of several of those trips, and the idle engine only
 * opens a run on a measured stationary sample, so three of the seven produced no episode at all.
 *
 * A trip with no moving sample is absent from the map and keeps its record start — an unmeasured
 * departure, not an invented one. Ends need no equivalent: a trip's `endTime` is its last
 * ignition-on sample, so it already includes the idling before shutdown.
 */
const firstMotionByTrip = (speedTrips) => new Map(
    (Array.isArray(speedTrips) ? speedTrips : [])
        .filter((row) => row?.tripId && row?.firstMotionTime)
        .map((row) => [row.tripId, moment(row.firstMotionTime).valueOf()])
        .filter(([, ts]) => Number.isFinite(ts)),
);

/**
 * One row per agency-local calendar day. Only days that carry trips appear; use
 * zeroFillDaily for the chart, which must show quiet days as zero rather than a gap.
 *
 * `analytics` is the optional { episodes, fuelTrips, speedTrips } payload from
 * GET /v3/trip/analytics. It is what supplies excess idling and peak speed; without it both come
 * back 0, which the table renders as "not measured" rather than as a real zero. `fuelTrips` upgrades
 * the fuel figure where the ledger reconciled one — see fuelLitres. `events` is deliberately not
 * read here: the Dayboard's Peak is the day's top speed, not its worst safety event.
 */
export const buildDayboard = (trips, timezone, analytics) => {
    const list = Array.isArray(trips) ? trips : [];
    const zone = timezone || 'UTC';
    const byDay = new Map();

    const fuelByTrip = toFuelIndex(analytics?.fuelTrips);
    const hasAnalytics = Boolean(analytics?.loaded);
    const idleByDay = foldIdleByLocalDay(analytics?.episodes, zone);
    // Safety events, split by type the way fleetconsole's A / B / C / Speed columns read them.
    const accelByDay = countByLocalDay(analytics?.events, zone, (row) => row.type === 'HARSH_ACCEL');
    const brakeByDay = countByLocalDay(analytics?.events, zone, (row) => row.type === 'HARSH_BRAKE');
    const cornerByDay = countByLocalDay(analytics?.events, zone, (row) => row.type === 'HARSH_CORNER');
    const overspeedByDay = countByLocalDay(analytics?.events, zone, (row) => row.type === 'OVERSPEED');
    const refuelByDay = countByLocalDay(analytics?.fuelEvents, zone, (row) => row.type === 'REFILL');
    const drainByDay = countByLocalDay(analytics?.fuelEvents, zone, (row) => row.type === 'DRAIN');
    // Peak is the day's fastest telemetry sample, folded up from per-trip maxima — not the fastest
    // safety event, which is what this column used to show. A 169 km/h run carrying no greenDriving
    // or overspeeding flag read as 86 km/h under the old fold. Trip-keyed, so every cross-filter
    // narrows it; the Safety tab's own peakKmh stays event-scoped, where it belongs.
    const peakByTrip = new Map(
        (Array.isArray(analytics?.speedTrips) ? analytics.speedTrips : [])
            .filter((row) => row?.tripId)
            .map((row) => [row.tripId, num(row.maxKmh)]),
    );

    // Trips are still bucketed by their record start: a trip beginning at 23:58 and pulling away
    // after midnight belongs to the day it was dispatched, the same day every other column counts it
    // in. Only the reported time moves.
    const motionByTrip = firstMotionByTrip(analytics?.speedTrips);

    list.forEach((trip) => {
        if (!trip?.startTime) return;
        const start = moment.tz(trip.startTime, zone);
        if (!start.isValid()) return;
        const key = start.format('YYYY-MM-DD');
        if (!byDay.has(key)) byDay.set(key, []);
        byDay.get(key).push(trip);
    });

    // A day earns a row from *any* activity, not only from trips. An asset that sat all day with
    // the engine running made no journey but burnt fuel doing it — seeding the day set from trips
    // alone computed that idling and then dropped it on the floor, hiding exactly the waste this
    // table exists to show. Safety events and fuel drains do the same: a drain detected on a day
    // the vehicle never moved is the theft case, and it must not vanish for want of a trip.
    [idleByDay, accelByDay, brakeByDay, cornerByDay, overspeedByDay, refuelByDay, drainByDay]
        .forEach((source) => source.forEach((_value, day) => {
            if (!byDay.has(day)) byDay.set(day, []);
        }));

    return Array.from(byDay.keys()).sort().map((day) => {
        const dayTrips = byDay.get(day);
        const startTimes = dayTrips.map(
            (t) => motionByTrip.get(t.startTraceId) ?? moment.tz(t.startTime, zone).valueOf(),
        );
        const endTimes = dayTrips
            .filter((t) => t.endTime)
            .map((t) => moment.tz(t.endTime, zone).valueOf());

        // Null on an idling-only day: there was no first movement and no last shutdown to report,
        // and `Math.min()` over nothing is Infinity, which would render as an invalid date.
        const firstTs = startTimes.length ? Math.min(...startTimes) : null;
        let lastTs = null;
        if (endTimes.length) lastTs = Math.max(...endTimes);
        else if (startTimes.length) lastTs = Math.max(...startTimes);

        // Carries fuelLitre, fuelBasis and driveSec — see summariseTrips. `driveSec` is time
        // inside a trip with the vehicle actually moving, so it is disjoint from the idle figure
        // below and the two sum to engine-on time. fleetconsole renders whole trip duration in
        // this column instead, which already contains the in-trip idling it sits beside.
        const summary = summariseTrips(dayTrips, fuelByTrip);
        const dayIdle = idleByDay.get(day);
        // Window-wide idling that overlaps this day, including time parked between trips. Falls
        // back to the in-trip figure when no analytics payload was loaded — an undercount, not a
        // different unit.
        const idleSec = hasAnalytics
            ? Math.round(dayIdle?.idleSec || 0)
            : Math.round(idleSeconds(dayTrips));
        const engineOnSec = summary.driveSec + idleSec;
        const events = hasAnalytics ? {
            accelEvents: accelByDay.get(day) || 0,
            brakeEvents: brakeByDay.get(day) || 0,
            cornerEvents: cornerByDay.get(day) || 0,
            overspeedEvents: overspeedByDay.get(day) || 0,
            refuels: refuelByDay.get(day) || 0,
            drains: drainByDay.get(day) || 0,
        } : {
            accelEvents: null,
            brakeEvents: null,
            cornerEvents: null,
            overspeedEvents: null,
            refuels: null,
            drains: null,
        };

        return {
            id: day,
            day,
            // `day` stays ISO — it is the filter key every other view matches on. Only the label
            // is day-first, and it is zero-padded so a month of rows stays column-aligned.
            dayLabel: moment.tz(day, 'YYYY-MM-DD', zone).format('DD-MM-YYYY'),
            firstTs,
            lastTs,
            firstLast: firstTs === null
                ? '—'
                : `${moment.tz(firstTs, zone).format('HH:mm:ss')} → ${moment.tz(lastTs, zone).format('HH:mm:ss')}`,
            ...summary,
            ...events,
            idleSec,
            engineOnSec,
            idleSharePct: engineOnSec > 0 ? (100 * idleSec) / engineOnSec : 0,
            excessSec: Math.round(dayIdle?.excessSec || 0),
            peakKmh: dayTrips.reduce(
                (peak, t) => Math.max(peak, peakByTrip.get(t.startTraceId) || 0),
                0,
            ),
        };
    });
};

/**
 * Daily trip counts across the whole requested window, zero-filled.
 *
 * The API's own tripsPerHour is sparse — bigquery.js GROUP BYs on hour, so hours with
 * no trips are simply absent. Charting it raw makes a quiet Sunday look like missing
 * data instead of a rest day.
 */
export const zeroFillDaily = (trips, fromTime, toTime, timezone) => {
    const zone = timezone || 'UTC';
    const from = moment.tz(fromTime, zone);
    const to = moment.tz(toTime, zone);
    if (!from.isValid() || !to.isValid() || to.isBefore(from)) return [];

    const counts = new Map();
    (Array.isArray(trips) ? trips : []).forEach((trip) => {
        if (!trip?.startTime) return;
        const start = moment.tz(trip.startTime, zone);
        if (!start.isValid()) return;
        const key = start.format('YYYY-MM-DD');
        counts.set(key, (counts.get(key) || 0) + 1);
    });

    const series = [];
    const cursor = from.clone().startOf('day');
    const last = to.clone().startOf('day');
    // 31-day server cap (TELEMETRYLOG_QUERY_PERIOD_LIMIT) bounds this loop in practice;
    // 400 is a defensive ceiling in case a caller passes a wider window.
    let guard = 0;
    while (cursor.isSameOrBefore(last) && guard < 400) {
        const key = cursor.format('YYYY-MM-DD');
        series.push([cursor.valueOf(), counts.get(key) || 0]);
        cursor.add(1, 'day');
        guard += 1;
    }
    return series;
};

/**
 * Trips grouped by origin → destination. Pairing is exact-string on the pre-flattened
 * origin/destination values, because bigquery.js:1179 collapses the structured
 * origin.{road,suburb,city} struct into one string before it reaches us. Grouping by
 * city needs that projection widened server-side.
 */
export const buildOdPairs = (trips, fuelTrips) => {
    const list = Array.isArray(trips) ? trips : [];
    const fuelByTrip = toFuelIndex(fuelTrips);
    const byPair = new Map();

    list.forEach((trip) => {
        const origin = placeLabel(trip?.origin);
        const destination = placeLabel(trip?.destination);
        const key = `${origin} ${destination}`;
        if (!byPair.has(key)) byPair.set(key, { origin, destination, trips: [] });
        byPair.get(key).trips.push(trip);
    });

    return Array.from(byPair.values())
        .map((pair) => ({
            id: `${pair.origin} → ${pair.destination}`,
            origin: pair.origin,
            destination: pair.destination,
            pairLabel: `${pair.origin} → ${pair.destination}`,
            ...summariseTrips(pair.trips, fuelByTrip),
        }))
        .sort((a, b) => b.trips - a.trips
            || b.distanceKm - a.distanceKm
            || a.pairLabel.localeCompare(b.pairLabel));
};

/**
 * Gaps between consecutive trips of the same asset — the vehicle was somewhere, doing
 * something, and neither the trip list nor the map shows it today.
 *
 * Trips are sorted by start time per asset first; the API returns them ordered by hour
 * then start time, which is already sorted for one asset but not across a fan-out.
 */
export const buildDwellSegments = (trips) => {
    const list = (Array.isArray(trips) ? trips : []).filter((t) => t?.startTime);
    const byAsset = new Map();
    list.forEach((trip) => {
        const key = trip.assetName || trip.deviceSerial || '-';
        if (!byAsset.has(key)) byAsset.set(key, []);
        byAsset.get(key).push(trip);
    });

    const segments = [];
    byAsset.forEach((assetTrips) => {
        const sorted = [...assetTrips].sort(
            (a, b) => moment(a.startTime).valueOf() - moment(b.startTime).valueOf(),
        );
        for (let i = 0; i < sorted.length - 1; i += 1) {
            const previous = sorted[i];
            const next = sorted[i + 1];
            if (!previous.endTime) continue; // eslint-disable-line no-continue
            const endTs = moment(previous.endTime).valueOf();
            const startTs = moment(next.startTime).valueOf();
            const dwellSec = Math.round((startTs - endTs) / 1000);
            // Negative means the two trips overlap — a segmentation artefact upstream, not a dwell.
            if (!Number.isFinite(dwellSec) || dwellSec < 0) continue; // eslint-disable-line no-continue
            segments.push({
                assetName: previous.assetName || previous.deviceSerial || '-',
                afterTripId: previous.startTraceId,
                beforeTripId: next.startTraceId,
                place: placeLabel(previous.destination),
                arrivedAt: previous.endTime,
                departedAt: next.startTime,
                dwellSec,
                // A dwell that starts and ends in the same named place is a genuine stop;
                // differing names usually mean the vehicle moved untracked between them.
                samePlace: placeLabel(previous.destination) === placeLabel(next.origin),
            });
        }
    });

    return segments.sort((a, b) => moment(a.arrivedAt).valueOf() - moment(b.arrivedAt).valueOf());
};

/** Trips whose start falls on the given agency-local calendar day. */
export const filterTripsByDay = (trips, day, timezone) => {
    if (!day) return trips;
    const zone = timezone || 'UTC';
    return (Array.isArray(trips) ? trips : []).filter(
        (trip) => trip?.startTime && moment.tz(trip.startTime, zone).format('YYYY-MM-DD') === day,
    );
};

/** Trips inside an epoch-millisecond window, as published by an ApexCharts brush zoom. */
export const filterTripsByRange = (trips, range) => {
    if (!range || !Number.isFinite(range.min) || !Number.isFinite(range.max)) return trips;
    return (Array.isArray(trips) ? trips : []).filter((trip) => {
        if (!trip?.startTime) return false;
        const ts = moment(trip.startTime).valueOf();
        return ts >= range.min && ts <= range.max;
    });
};

/** Trips matching one origin → destination pair. */
export const filterTripsByPair = (trips, pair) => {
    if (!pair?.origin || !pair?.destination) return trips;
    return (Array.isArray(trips) ? trips : []).filter(
        (trip) => placeLabel(trip?.origin) === pair.origin
            && placeLabel(trip?.destination) === pair.destination,
    );
};

/**
 * Display number per trip, keyed on startTraceId — the same `tripId` the analytics
 * endpoint stamps on every event and episode.
 *
 * TripRail numbers trips `#1..#n` over the *filtered* list, while the server's `tripNo`
 * indexes the whole requested window, so the two diverge the moment a cross-filter is
 * active. tripId is the contract; the number is a convenience, and it is re-derived here
 * rather than trusted.
 */
export const buildTripNumbers = (trips) => new Map(
    (Array.isArray(trips) ? trips : [])
        .filter((trip) => trip?.startTraceId)
        .map((trip, index) => [trip.startTraceId, index + 1]),
);

const EVENT_TITLES = {
    HARSH_ACCEL: 'Harsh acceleration',
    HARSH_BRAKE: 'Harsh braking',
    HARSH_CORNER: 'Harsh cornering',
    OVERSPEED: 'Overspeeding',
};

const IDLE_TITLES = { 400: 'Idle', 450: 'Excessive idle', 460: 'Excusable idle' };

const FUEL_TITLES = { REFILL: 'Refuel', DRAIN: 'Fuel drain' };

/** Seconds between two ISO instants, or null. Ledger rows carry ends, not durations. */
const spanSec = (startTime, endTime) => {
    const from = Date.parse(startTime);
    const to = Date.parse(endTime);
    if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
    return Math.max(0, Math.round((to - from) / 1000));
};

/**
 * `2026-06-28 13:34 UTC+8` — a map popup's timestamp, fleetconsole's format.
 *
 * The offset is the point of it. Everything else on this panel reads in the agency timezone
 * because the panel says so once, at the top; a popup floating over a map carries no such frame,
 * and a fleet manager reading a refuel time needs to know it is not their own clock. ISO order
 * here rather than the DD-MM-YYYY the tables use, because that is the format this card was
 * specified against.
 */
const stampWithOffset = (value, zone) => {
    if (!value) return '';
    const at = moment.tz(value, zone || 'UTC');
    if (!at.isValid()) return '';
    // 'Z' gives '+08:00'; the card wants 'UTC+8'. Minutes are kept only where a zone has them —
    // Kathmandu is +5:45 — and parsed rather than regex-stripped, which ate the 0 of UTC+0.
    const [, sign, hours, minutes] = at.format('Z').match(/^([+-])(\d{2}):(\d{2})$/);
    const offset = `${sign}${Number(hours)}${minutes === '00' ? '' : `:${minutes}`}`;
    return `${at.format('YYYY-MM-DD HH:mm')} UTC${offset}`;
};

/**
 * A Safety or Idle row → the map's focus descriptor, or null when it has no position.
 *
 * One function rather than one per view: both rows carry `lat`/`lon`/`tripId` and both end up in
 * the same map popup, so the only thing that differs is which figures are worth reading. Pure and
 * exported so the popup's wording is testable without a map.
 *
 * `lines` is what the popup lists under the title. Absent figures are dropped rather than dashed —
 * a popup is a label, and a column of em dashes in one is noise the table already carries better.
 *
 * @param {Object} row
 * @param {'event'|'idle'|'fuel'|'charge'} kind
 * @param {string} zone Agency timezone.
 * @returns {?{lat: number, lon: number, tripId: ?string, title: string, when: string,
 *   lines: string[], address: ?string}}
 */
export const buildFocus = (row, kind, zone, tripNo) => {
    const lat = numberOrNull(row?.lat);
    const lon = numberOrNull(row?.lon);
    if (lat === null || lon === null) return null;

    // numberOrNull throughout, not num: a null g-force printed as "0.00 g" reads as a measured
    // non-event, which is the same trap OdEcoPill documents for a null eco score.
    const kmh = numberOrNull(row?.peakKmh);
    const litres = numberOrNull(row?.litres);
    const seconds = numberOrNull(row?.durationSec) ?? spanSec(row?.startTime, row?.endTime);

    // The headline figure, carried by the title. A ledger entry's whole point is how much moved,
    // so it belongs beside the label rather than buried in the detail line.
    let amount = '';
    if (kind === 'charge' && numberOrNull(row?.deltaPct) !== null) {
        amount = `+${Math.round(row.deltaPct)}%`;
    } else if (kind === 'fuel' && litres !== null) {
        // U+2212 MINUS SIGN, not a hyphen: at the size a map popup renders, a hyphen beside a
        // digit reads as a dash rather than a sign.
        amount = `${row.type === 'DRAIN' ? '\u2212' : '+'}${litres.toFixed(1)} L`;
    }

    const lines = [];
    if (kind === 'charge') {
        const end = numberOrNull(row.endPct);
        if (end !== null) lines.push(`${Math.round(end)}% SOC after`);
        if (seconds > 0) lines.push(compactDuration(seconds));
    } else if (kind === 'fuel') {
        const after = numberOrNull(row.levelAfterL);
        lines.push(after !== null ? `${after.toFixed(1)} L after` : 'level unknown');
        if (seconds > 0) lines.push(compactDuration(seconds));
        // Engine state is evidence on a drain and means nothing on a fill — see tripFuel.
        if (row.engineOn === true) lines.push('engine running');
    } else if (kind === 'idle') {
        if (seconds !== null) lines.push(compactDuration(seconds));
        if (numberOrNull(row.excessSec) > 0) lines.push(`${compactDuration(row.excessSec)} over the limit`);
        if (numberOrNull(row.excusableSec) > 0) lines.push(`${compactDuration(row.excusableSec)} excused`);
        if (row.reason) lines.push(row.reason);
    } else if (row?.type === 'OVERSPEED') {
        const from = numberOrNull(row.startKmh);
        if (kmh !== null) lines.push(`peak ${Math.round(kmh)} km/h`);
        if (from !== null) lines.push(`from ${Math.round(from)} km/h`);
        if (seconds > 0) lines.push(compactDuration(seconds));
        if (row.closedBy) lines.push(row.closedBy);
    } else {
        const g = numberOrNull(row?.g);
        if (g !== null) lines.push(`${g.toFixed(2)} g`);
        if (kmh !== null) lines.push(`${Math.round(kmh)} km/h`);
        if (seconds > 0) lines.push(compactDuration(seconds));
    }

    let label;
    if (kind === 'charge') label = 'Recharge';
    else if (kind === 'fuel') label = FUEL_TITLES[row?.type] || String(row?.type ?? 'Fuel event');
    else if (kind === 'idle') label = IDLE_TITLES[row.classCode] || `Idle ${row.classCode ?? ''}`.trim();
    else label = EVENT_TITLES[row?.type] || String(row?.type ?? 'Event');

    return {
        lat,
        lon,
        tripId: row?.tripId || null,
        title: amount ? `${label} ${amount}` : label,
        when: stampWithOffset(row?.startTime, zone),
        // "between trips" is a fact, not a gap in the data: an overnight refuel or an idle in a
        // depot belongs to no journey, and saying so is what stops a reader hunting for the trip.
        trip: tripNo === undefined || tripNo === null ? 'between trips' : `trip #${tripNo}`,
        lines,
        address: row?.location || null,
    };
};

/** True when any trip in the set carries an EV/hybrid energy figure. */
export const hasEnergyData = (trips) => (Array.isArray(trips) ? trips : [])
    .some((trip) => num(trip?.energyUsedPerTrip) > 0);

/**
 * True when any trip in the set carries a fuel figure, from either source.
 *
 * The ledger has to be consulted, not just the trip record: a CAN vehicle never populates
 * `tripfuelLitre` — it reports percentage only — so a record-only test hides the fuel column on
 * exactly the fleets whose litres come from the analytics endpoint. See fuelLitres.
 */
export const hasFuelData = (trips, fuelTrips) => {
    const list = Array.isArray(trips) ? trips : [];
    if (list.some((trip) => num(trip?.totalFuelLitre ?? trip?.tripfuelLitre) > 0)) return true;
    const fuelByTrip = toFuelIndex(fuelTrips);
    return list.some((trip) => num(fuelByTrip.get(trip?.startTraceId)?.litres) > 0);
};
